import { useState } from 'react';
import { DeleteButton, EmployeePicker, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { EosIssue, IssuePriority, IssueTerm } from './eosTypes';
import type { BusinessUnitOption } from './eosUi';
import { BusinessUnitPicker } from './eosUi';

/**
 * The Issues List — short term and long term, worked through IDS.
 *
 * The original app's screen: a segmented short/long switch, a quick-add row,
 * and then the list in PRIORITY ORDER with a numbered square down the left and
 * up/down arrows to move an issue. That ordering is the point of the section —
 * IDS works the top issue first — so the list is explicitly ranked rather than
 * sorted by a dropdown.
 *
 * These are `coo.Issue`, the SAME issues as the V/TO's Issues List. The
 * long/short split is what that table's `Term` column was always for. See
 * migration 058.
 */
export function EosIssues({
  teamId,
  meetingId,
  onChanged,
}: {
  teamId: number;
  /** Set inside the meeting runner, so a solved issue records which L10 solved it. */
  meetingId?: number;
  onChanged: () => void;
}) {
  const issues = useResource<EosIssue[]>(`/eos/issues?teamId=${teamId}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [term, setTerm] = useState<IssueTerm>('short');
  const [title, setTitle] = useState('');
  const [solving, setSolving] = useState<EosIssue | null>(null);
  const [editing, setEditing] = useState<EosIssue | null>(null);
  const [businessUnitId, setBusinessUnit] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const units = useResource<BusinessUnitOption[]>('/eos/business-units');

  const all = issues.data ?? [];
  const inList = all.filter((i) => i.Term === term);
  /* Ranked by SortOrder — which is what the source app's integer `priority`
     column actually was, and what migration 058 mapped it onto. */
  const open = inList
    .filter((i) => i.Status === 'open' || i.Status === 'in_progress')
    .sort((a, b) => a.SortOrder - b.SortOrder || a.IssueId - b.IssueId);
  const closed = inList.filter((i) => i.Status === 'resolved' || i.Status === 'dropped');

  const add = async () => {
    if (!title.trim()) return;
    setError(null);
    try {
      await api.post('/eos/issues', {
        teamId,
        title: title.trim(),
        term,
        meetingId: meetingId ?? null,
        businessUnitId,
      });
      setTitle('');
      /* The unit stays selected — a run of issues raised in one IDS is
         usually about one part of the business, and re-picking it every time
         is how the field stops getting used. */
      issues.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that issue.');
    }
  };

  /** Swaps an issue with its neighbour and persists both positions. */
  const move = async (index: number, direction: -1 | 1) => {
    const a = open[index];
    const b = open[index + direction];
    if (!a || !b) return;
    setError(null);
    try {
      /* Both rows are written, because a swap that only moves one leaves the
         two sharing a position and the order stops being stable. */
      await Promise.all([
        api.put(`/eos/issues/${a.IssueId}`, { sortOrder: b.SortOrder }),
        api.put(`/eos/issues/${b.IssueId}`, { sortOrder: a.SortOrder }),
      ]);
      issues.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reorder.');
    }
  };

  return (
    <div className="eos">
      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-head row-between wrap">
          <div>
            <h3>Issues</h3>
            <p className="xs subtle">
              Identify, Discuss, Solve — top of the list first. These are the same issues as the
              V/TO&apos;s.
            </p>
          </div>
          <div className="eos-switch">
            {(['short', 'long'] as IssueTerm[]).map((t) => (
              <button
                key={t}
                type="button"
                className={term === t ? 'on' : ''}
                aria-pressed={term === t}
                onClick={() => setTerm(t)}
              >
                {t === 'short' ? 'Short term' : 'Long term'}{' '}
                <span className="xs subtle">
                  {
                    all.filter(
                      (i) => i.Term === t && (i.Status === 'open' || i.Status === 'in_progress'),
                    ).length
                  }
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="card-body">
          <form
            className="eos-quick-add"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <div className="grow">
              <input
                className="input"
                placeholder={`New ${term === 'short' ? 'short-term' : 'long-term'} issue — the problem, not the solution`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <BusinessUnitPicker
              units={units.data ?? []}
              value={businessUnitId}
              onChange={setBusinessUnit}
            />
            <button type="submit" className="btn btn-primary" disabled={!title.trim()}>
              Add
            </button>
          </form>

          {issues.loading && <Loading label="Loading issues" />}
          {issues.error && <ErrorBanner message={issues.error} />}

          <div className="eos-list mt-4">
            {open.length === 0 && !issues.loading && (
              <div className="muted">
                Nothing open on the {term === 'short' ? 'short-term' : 'long-term'} list.
              </div>
            )}

            {open.map((issue, index) => (
              <div className="eos-row" key={issue.IssueId}>
                <span className="eos-prio">{index + 1}</span>
                <div className="eos-move">
                  <button
                    type="button"
                    aria-label={`Move ${issue.Title} up`}
                    disabled={index === 0}
                    onClick={() => void move(index, -1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${issue.Title} down`}
                    disabled={index === open.length - 1}
                    onClick={() => void move(index, 1)}
                  >
                    ▼
                  </button>
                </div>

                <div className="grow">
                  <div className="title">{issue.Title}</div>
                  <div className="meta">
                    {issue.OwnerName ?? 'Unassigned'}
                    {issue.Status === 'in_progress' && ' · being worked'}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void api
                      .post(`/eos/issues/${issue.IssueId}/move`, {
                        term: issue.Term === 'short' ? 'long' : 'short',
                      })
                      .then(() => {
                        issues.reload();
                        onChanged();
                      })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not move it.'),
                      );
                  }}
                >
                  {issue.Term === 'short' ? 'Park long term' : 'Pull short term'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditing(issue)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSolving(issue)}
                >
                  Solve
                </button>
                <DeleteButton
                  label="✕"
                  what={`the issue "${issue.Title}"`}
                  onConfirm={() => {
                    void api
                      .del(`/eos/issues/${issue.IssueId}`)
                      .then(() => {
                        issues.reload();
                        onChanged();
                      })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not delete it.'),
                      );
                  }}
                />
              </div>
            ))}
          </div>

          {closed.length > 0 && (
            <>
              <div className="eos-section-title mt-4">Solved and dropped ({closed.length})</div>
              <div className="eos-list">
                {closed.map((issue) => (
                  <div className="eos-row is-done" key={issue.IssueId}>
                    <div className="grow">
                      <div className="title">{issue.Title}</div>
                      {issue.Resolution && <div className="meta">{issue.Resolution}</div>}
                    </div>
                    <span className={`eos-pill ${issue.Status === 'resolved' ? 'good' : 'neutral'}`}>
                      {issue.Status === 'resolved' ? 'Solved' : 'Dropped'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEditing(issue)}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {editing && (
        <EditIssueDialog
          issue={editing}
          employees={employees.data ?? []}
          units={units.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            issues.reload();
            onChanged();
          }}
        />
      )}

      {solving && (
        <SolveDialog
          issue={solving}
          teamId={teamId}
          meetingId={meetingId}
          employees={employees.data ?? []}
          onClose={() => setSolving(null)}
          onSolved={() => {
            setSolving(null);
            issues.reload();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/**
 * Solving an issue, and the to-do it usually creates.
 *
 * The to-do is offered rather than required: some issues are solved by a
 * decision and need nothing doing. But the common case in an L10 is "we agreed
 * X, and somebody has to go and do it", and making that a second trip to
 * another tab is how the doing gets lost.
 */
function SolveDialog({
  issue,
  teamId,
  meetingId,
  employees,
  onClose,
  onSolved,
}: {
  issue: EosIssue;
  teamId: number;
  meetingId?: number;
  employees: Employee[];
  onClose: () => void;
  onSolved: () => void;
}) {
  const [resolution, setResolution] = useState('');
  const [todoTitle, setTodoTitle] = useState('');
  const [todoOwner, setTodoOwner] = useState<number | null>(null);
  const [todoDue, setTodoDue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/eos/issues/${issue.IssueId}`, {
        status: 'resolved',
        resolution: resolution.trim() || null,
        /* Recorded only when solved inside a meeting — an issue solved from the
           tab between meetings has no meeting to point at. */
        ...(meetingId ? { meetingId } : {}),
      });
      if (todoTitle.trim()) {
        await api.post('/eos/todos', {
          teamId,
          title: todoTitle.trim(),
          ownerEmployeeId: todoOwner,
          dueDate: todoDue || null,
          meetingId: meetingId ?? null,
        });
      }
      onSolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not solve it.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Solve issue"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Solve'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="strong">{issue.Title}</p>

      <div className="mt-4">
        <Field label="What we decided">
          <textarea
            className="textarea"
            rows={3}
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
          />
        </Field>
      </div>

      <div className="divider mt-4" />
      <div className="eos-section-title">Does this create a to-do?</div>

      <div className="form-grid">
        <Field label="To-do" hint="Leave blank if nothing needs doing.">
          <input className="input" value={todoTitle} onChange={(e) => setTodoTitle(e.target.value)} />
        </Field>
        <Field label="Owner">
          <EmployeePicker employees={employees} value={todoOwner} onChange={setTodoOwner} />
        </Field>
        <Field label="Due">
          <input
            className="input"
            type="date"
            value={todoDue}
            onChange={(e) => setTodoDue(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Editing an issue after it was raised.
 *
 * A quick-add row is the right way to GET an issue onto the list — in an IDS
 * nobody wants a form — but it captures a title and nothing else, and the
 * thing typed in thirty seconds is routinely half a sentence, filed under the
 * wrong unit, or owned by nobody. Everything the add row skips is here, so
 * fixing it is a click on the row rather than delete-and-retype, which is what
 * people were doing instead and which loses the issue's place in the ranking.
 *
 * Solved and dropped issues open this too, because the field most worth
 * correcting after the fact is the one written last: what we decided.
 */
function EditIssueDialog({
  issue,
  employees,
  units,
  onClose,
  onSaved,
}: {
  issue: EosIssue;
  employees: Employee[];
  units: BusinessUnitOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const closed = issue.Status === 'resolved' || issue.Status === 'dropped';

  const [title, setTitle] = useState(issue.Title);
  const [description, setDescription] = useState(issue.Description ?? '');
  const [ownerEmployeeId, setOwner] = useState<number | null>(issue.OwnerEmployeeId);
  const [priority, setPriority] = useState<IssuePriority>(issue.Priority);
  const [businessUnitId, setBusinessUnit] = useState<number | null>(issue.BusinessUnitId);
  const [resolution, setResolution] = useState(issue.Resolution ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      /* Status is deliberately not sent. It is what Solve, the term switch and
         the delete button are for, and putting it in a general edit form is
         how an issue gets closed by somebody who meant to fix a typo. */
      await api.put(`/eos/issues/${issue.IssueId}`, {
        title: title.trim(),
        description: description.trim() || null,
        ownerEmployeeId,
        priority,
        businessUnitId,
        ...(closed ? { resolution: resolution.trim() || null } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that issue.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit issue"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Issue" hint="The problem, not the solution.">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <div className="mt-4">
        <Field label="Detail" hint="Optional — the context somebody reading this in a month needs.">
          <textarea
            className="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>

      <div className="form-grid mt-4">
        <Field label="Owner">
          <EmployeePicker employees={employees} value={ownerEmployeeId} onChange={setOwner} />
        </Field>
        <Field label="Priority">
          <select
            className="select"
            value={priority}
            onChange={(e) => setPriority(e.target.value as IssuePriority)}
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Business unit">
          <BusinessUnitPicker units={units} value={businessUnitId} onChange={setBusinessUnit} />
        </Field>
      </div>

      {closed && (
        <div className="mt-4">
          <Field label="What we decided">
            <textarea
              className="textarea"
              rows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}
